import { type EditorAnalyticsAPI, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import { type InvokedFrom } from '../analytics/analytics-flow/analyticsFlowTypes';
import { type EditorAIAnalyticEventPayload } from '../analytics/types';
import type { EditorPluginAIConfigItemMarkdown, Positions } from '../config-items/config-items';
import { getHasAcceptableUseWarning } from '../experience-application/screens-with-logic/utils/get-has-acceptable-use-warning';
import { createOpenAIModalCommand } from '../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../types';

import type { GetInitialContextProps } from './types';

export const getAIPanelContext = ({
	configItem,
	intentSchemaId,
	editorView,
	providerFactory,
	idMap,
	intl,
	updateIdMap,
}: GetInitialContextProps) => {
	if (!editorView) {
		return;
	}
	const positions: Positions = [
		editorView.state.selection.$from.pos,
		editorView.state.selection.$to.pos,
	];

	const initialContext = configItem.getInitialContext?.({
		editorView,
		positions,
		intl,
		updateIdMap,
		intentSchemaId,
	});

	return {
		schema: editorView.state.schema,
		providerFactory,
		idMap,
		initialContext,
	};
};

type FeedbackCollectorParams = {
	extensionKey: string;
	markdown: string;
	locale: string;
	editorPluginAIProvider?: EditorPluginAIProvider;
	fireAnalyticsEvent?: EditorAnalyticsAPI['fireAnalyticsEvent'];
};

const trackAnalyticsEvent = (
	fireAnalyticsEvent: EditorAnalyticsAPI['fireAnalyticsEvent'],
	product: EditorPluginAIProvider['product'],
	extensionKey: string,
	productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown',
	hasAcceptableUseWarning?: boolean,
) => {
	const payload: EditorAIAnalyticEventPayload = {
		action: 'click',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'AIPanelsFeedbackButton',
		attributes: {
			extensionKey,
			sentiment: 'unknown',
			hasAcceptableUseWarning,
			appearance: 'unknown',
			product,
			productSuccessStatus,
		},
		eventType: EVENT_TYPE.UI,
	};

	// @ts-ignore
	fireAnalyticsEvent(payload);
};

export const triggerAIPanelFeedbackCollector = async ({
	extensionKey,
	markdown,
	locale,
	editorPluginAIProvider,
	fireAnalyticsEvent,
}: FeedbackCollectorParams) => {
	const hasAcceptableUseWarning = getHasAcceptableUseWarning({
		markdown,
		locale,
	});

	if (!editorPluginAIProvider || !fireAnalyticsEvent) {
		return;
	}

	try {
		const productResponse = await editorPluginAIProvider.handleFeedbackSubmission?.({
			sentiment: 'unknown',
			getAIExperience: (hasUserConsent?: boolean) => ({
				hasAcceptableUseWarning,
				extensionKey,
				...(hasUserConsent ? { markdownResponse: markdown } : {}),
			}),
			editorAttributes: {
				appearance: 'unknown',
				product: editorPluginAIProvider.product,
			},
		});

		trackAnalyticsEvent(
			fireAnalyticsEvent,
			editorPluginAIProvider.product,
			extensionKey,
			productResponse?.status,
			hasAcceptableUseWarning,
		);
	} catch (err) {
		trackAnalyticsEvent(
			fireAnalyticsEvent,
			editorPluginAIProvider.product,
			extensionKey,
			'failed',
			hasAcceptableUseWarning,
		);
	}
};

export const openAIModal = ({
	invokedFrom,
	aiOptIn,
	configItem,
	editorView,
}: {
	invokedFrom: InvokedFrom;
	aiOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItemMarkdown;
	editorView?: EditorView;
}) => {
	if (!editorView) {
		return;
	}

	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		lastTriggeredFrom: invokedFrom,
		aiGlobalOptIn: aiOptIn,
	});

	openAIModalCommand(editorView.state, editorView.dispatch);
};
